import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getPontajDay, savePontajDay, getPontajHistory } from '../../services/pontaj';
import { listClienti } from '../../services/clienti';
import { listAllProceduri } from '../../services/proceduri';
import { FilterPill } from '../../components/Shared/FilterPill';
import DateField from '../../components/Shared/DateField';
import '../Clienti/ClientiPage.css';
import './PontajPage.css';

const CATEGORII = [
  ['facturabil',   'Facturabil'],
  ['nefacturabil', 'Nefacturabil'],
  ['inclus',       'Inclus in onorariu fix'],
];
const CATEGORIE_LABEL = Object.fromEntries(CATEGORII);

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function shiftDay(iso, delta) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDayLabel(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('ro-RO', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}

function isAfter4pmWeekday() {
  const now = new Date();
  return now.getDay() >= 1 && now.getDay() <= 5 && now.getHours() >= 16;
}

function fmtDayShort(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('ro-RO', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
}

let _rowSeq = 0;
const newRowKey = () => `row_${Date.now()}_${_rowSeq++}`;

function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg) => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

function procLabel(p) {
  return `${p.data?.denumire || p.data?.numar || p.tip_proc} — ${p.client_name || ''}`;
}

const CalendarTriggerButton = React.forwardRef(({ onClick }, ref) => (
  <button type="button" className="pj-cal-btn" onClick={onClick} ref={ref} title="Alege data">
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <path d="M16 2v4M8 2v4M3 10h18"/>
    </svg>
  </button>
));

export default function PontajPage() {
  const [day, setDay] = useState(todayISO());
  const [clienti, setClienti] = useState([]);
  const [allProceduri, setAllProceduri] = useState([]);
  const [rows, setRows] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toasts, show: showToast } = useToast();

  // ── Istoric ──
  const [histDays, setHistDays] = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [histLoadingMore, setHistLoadingMore] = useState(false);
  const [histHasMore, setHistHasMore] = useState(false);
  const [histNextBefore, setHistNextBefore] = useState(null);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterActive, setFilterActive] = useState(false);

  useEffect(() => {
    listClienti().then(res => setClienti(res.clients || [])).catch(() => {});
    listAllProceduri([]).then(res => setAllProceduri(res.proceduri || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPontajDay(day).then(res => {
      if (cancelled) return;
      setRows((res.entries || []).map(e => ({
        _key: `e${e.id}`, id: e.id,
        client_id: e.client_id, procedura_id: e.procedura_id,
        activitate: e.activitate, categorie: e.categorie, ore: e.ore,
        sursa_tip: e.sursa_tip, sursa_id: e.sursa_id,
      })));
      setSuggestions(res.suggestions || []);
      setSubmittedAt(res.submitted_at);
    }).catch(() => showToast('Eroare la incarcarea pontajului')).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [day, showToast]);

  const loadHistoryInitial = useCallback(() => {
    setHistLoading(true);
    setFilterActive(false);
    getPontajHistory({ days: 25 }).then(res => {
      setHistDays(res.days || []);
      setHistHasMore(!!res.has_more);
      setHistNextBefore(res.next_before);
    }).catch(() => showToast('Eroare la incarcarea istoricului')).finally(() => setHistLoading(false));
  }, [showToast]);

  useEffect(() => { loadHistoryInitial(); }, [loadHistoryInitial]);

  const loadHistoryMore = () => {
    if (!histNextBefore) return;
    setHistLoadingMore(true);
    getPontajHistory({ before: histNextBefore, days: 25 }).then(res => {
      setHistDays(prev => [...prev, ...(res.days || [])]);
      setHistHasMore(!!res.has_more);
      setHistNextBefore(res.next_before);
    }).catch(() => showToast('Eroare la incarcarea istoricului')).finally(() => setHistLoadingMore(false));
  };

  const applyHistoryFilter = () => {
    if (!filterFrom || !filterTo) {
      showToast('Alege ambele date pentru interval');
      return;
    }
    setHistLoading(true);
    getPontajHistory({ from: filterFrom, to: filterTo }).then(res => {
      setHistDays(res.days || []);
      setHistHasMore(false);
      setHistNextBefore(null);
      setFilterActive(true);
    }).catch(() => showToast('Eroare la incarcarea istoricului')).finally(() => setHistLoading(false));
  };

  const resetHistoryFilter = () => {
    setFilterFrom('');
    setFilterTo('');
    loadHistoryInitial();
  };

  const clientName = useCallback((id) => clienti.find(c => c.id === id)?.nume || '', [clienti]);

  const procOptsForRow = useCallback((clientId) => {
    const filtered = clientId ? allProceduri.filter(p => p.client_id === clientId) : allProceduri;
    return filtered.map(p => ({ id: p.id, label: procLabel(p) }));
  }, [allProceduri]);

  const addSuggestion = (s) => {
    setRows(prev => [...prev, {
      _key: newRowKey(), id: null,
      client_id: s.client_id, procedura_id: s.procedura_id,
      activitate: s.activitate, categorie: 'facturabil', ore: '',
      sursa_tip: s.sursa_tip, sursa_id: s.sursa_id,
    }]);
    setSuggestions(prev => prev.filter(x => !(x.sursa_tip === s.sursa_tip && x.sursa_id === s.sursa_id)));
  };

  const addBlankRow = () => {
    setRows(prev => [...prev, {
      _key: newRowKey(), id: null,
      client_id: null, procedura_id: null,
      activitate: '', categorie: 'facturabil', ore: '',
      sursa_tip: '', sursa_id: null,
    }]);
  };

  const updateRow = (key, patch) => {
    setRows(prev => prev.map(r => r._key === key ? { ...r, ...patch } : r));
  };

  const handleClientChange = (key, clientId) => {
    setRows(prev => prev.map(r => {
      if (r._key !== key) return r;
      const stillValid = clientId ? allProceduri.some(p => p.id === r.procedura_id && p.client_id === clientId) : true;
      return { ...r, client_id: clientId, procedura_id: stillValid ? r.procedura_id : null };
    }));
  };

  const deleteRow = (key) => {
    setRows(prev => prev.filter(r => r._key !== key));
  };

  const totalOre = useMemo(
    () => rows.reduce((sum, r) => sum + (parseFloat(r.ore) || 0), 0),
    [rows],
  );

  const jumpToDay = (iso) => {
    setDay(iso);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    const invalid = rows.some(r => !r.activitate.trim() || !(parseFloat(r.ore) > 0));
    if (invalid) {
      showToast('Completeaza activitatea si orele pentru fiecare linie (sau sterge-o) inainte de a trimite');
      return;
    }
    setSaving(true);
    try {
      const payload = rows.map(r => ({
        id: r.id, client_id: r.client_id, procedura_id: r.procedura_id,
        activitate: r.activitate.trim(), categorie: r.categorie, ore: r.ore,
        sursa_tip: r.sursa_tip, sursa_id: r.sursa_id,
      }));
      const res = await savePontajDay(day, payload);
      setRows((res.entries || []).map(e => ({
        _key: `e${e.id}`, id: e.id,
        client_id: e.client_id, procedura_id: e.procedura_id,
        activitate: e.activitate, categorie: e.categorie, ore: e.ore,
        sursa_tip: e.sursa_tip, sursa_id: e.sursa_id,
      })));
      setSubmittedAt(res.submitted_at);
      showToast('Pontaj trimis');
      if (filterActive) applyHistoryFilter(); else loadHistoryInitial();
    } catch {
      showToast('Eroare la trimiterea pontajului');
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="cp-main">
      <div className="topbar">
        <div className="topbar-left">
          <h1 className="page-title">Pontaj</h1>
        </div>
      </div>

      <div className="content">

      <div className="filter-bar">
        <button className="btn btn-sm" type="button" onClick={() => setDay(d => shiftDay(d, -1))}>‹</button>
        <div className="pj-day-label">
          {fmtDayLabel(day)}
          {day === todayISO() && <span className="pj-today-badge">azi</span>}
        </div>
        <button className="btn btn-sm" type="button" onClick={() => setDay(d => shiftDay(d, 1))} disabled={day >= todayISO()}>›</button>
        <DateField
          value={day}
          onChange={v => v && setDay(v)}
          customInput={<CalendarTriggerButton />}
          isClearable={false}
          maxDate={new Date()}
        />
        {day !== todayISO() && (
          <button className="btn btn-sm btn-link" type="button" onClick={() => setDay(todayISO())}>Azi</button>
        )}
        {submittedAt && (
          <span className="pj-submitted-badge">
            Trimis {new Date(submittedAt).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {!loading && day === todayISO() && !submittedAt && isAfter4pmWeekday() && (
        <div className="pj-late-banner">
          Nu ai trimis inca pontajul de azi — completeaza si apasa "Trimite" mai jos.
        </div>
      )}

      {loading ? (
        <div className="clients-card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>Se incarca...</div>
      ) : (
        <>
          {suggestions.length > 0 && (
            <div className="clients-card pj-suggestions-card">
              <div className="pj-section-title">Activitate detectata azi — adauga ce corespunde</div>
              {suggestions.map(s => (
                <div key={`${s.sursa_tip}_${s.sursa_id}`} className="pj-suggestion-row">
                  <span className="pj-sugg-text">
                    {s.activitate}
                    {s.client_id && <span className="pj-sugg-client"> — {clientName(s.client_id)}</span>}
                  </span>
                  <button className="btn btn-sm btn-secondary" type="button" onClick={() => addSuggestion(s)}>+ Adauga</button>
                </div>
              ))}
            </div>
          )}

          <div className="clients-card">
            <div className="pj-rows-head">
              <span>Client</span>
              <span>Procedura</span>
              <span>Activitate</span>
              <span>Categorie</span>
              <span>Ore</span>
              <span />
            </div>

            {rows.length === 0 && (
              <p style={{ padding: '20px 4px', color: 'var(--muted)', fontSize: 13 }}>
                Nicio activitate adaugata inca pentru aceasta zi.
              </p>
            )}

            {rows.map(r => (
              <div key={r._key} className="pj-row">
                <FilterPill
                  searchable
                  className="fp-wrap--full"
                  value={r.client_id ? String(r.client_id) : ''}
                  onChange={e => handleClientChange(r._key, e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">— Client —</option>
                  {clienti.map(c => <option key={c.id} value={String(c.id)}>{c.nume}</option>)}
                </FilterPill>

                <FilterPill
                  searchable
                  className="fp-wrap--full"
                  value={r.procedura_id ? String(r.procedura_id) : ''}
                  onChange={e => updateRow(r._key, { procedura_id: e.target.value ? Number(e.target.value) : null })}
                >
                  <option value="">— Procedura —</option>
                  {procOptsForRow(r.client_id).map(o => <option key={o.id} value={String(o.id)}>{o.label}</option>)}
                </FilterPill>

                <input
                  className="pj-input"
                  type="text"
                  value={r.activitate}
                  placeholder="Descrie activitatea..."
                  onChange={e => updateRow(r._key, { activitate: e.target.value })}
                />
                <select
                  className="pj-select"
                  value={r.categorie}
                  onChange={e => updateRow(r._key, { categorie: e.target.value })}
                >
                  {CATEGORII.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input
                  className="pj-hours"
                  type="number"
                  min="0" step="0.25"
                  value={r.ore}
                  placeholder="0.00"
                  onChange={e => updateRow(r._key, { ore: e.target.value })}
                />
                <button className="pj-row-del" type="button" onClick={() => deleteRow(r._key)} title="Sterge">✕</button>
              </div>
            ))}

            <div className="pj-rows-footer">
              <button className="btn btn-sm btn-secondary" type="button" onClick={addBlankRow}>+ Adauga activitate</button>
              <span className="pj-total">Total zi: <strong>{totalOre.toFixed(2)}h</strong></span>
            </div>
          </div>

          <div className="pj-submit-bar">
            <button className="btn btn-primary" type="button" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Se trimite...' : 'Trimite'}
            </button>
          </div>
        </>
      )}

      {/* ── Istoric ── */}
      <div className="section-label pj-hist-label">Istoric</div>
      <div className="filter-bar pj-hist-filter-bar">
        <DateField value={filterFrom} onChange={setFilterFrom} placeholder="De la" />
        <DateField value={filterTo} onChange={setFilterTo} placeholder="Pana la" />
        <button className="btn btn-sm btn-secondary" type="button" onClick={applyHistoryFilter}>Aplica filtru</button>
        {filterActive && <button className="btn btn-sm btn-link" type="button" onClick={resetHistoryFilter}>Reseteaza</button>}
      </div>

      {histLoading ? (
        <div className="clients-card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>Se incarca...</div>
      ) : histDays.length === 0 ? (
        <div className="clients-card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          Niciun pontaj gasit {filterActive ? 'in intervalul selectat' : 'in ultimele zile'}.
        </div>
      ) : (
        <div className="clients-card pj-hist-card">
          {histDays.map(hd => (
            <div key={hd.date} className="pj-hist-day">
              <div className="pj-hist-day-head" onClick={() => jumpToDay(hd.date)} title="Deschide aceasta zi pentru editare">
                <span className="pj-hist-date">{fmtDayShort(hd.date)}</span>
                {hd.submitted_at && <span className="pj-submitted-badge pj-submitted-badge--sm">trimis</span>}
                <span className="pj-hist-total">{hd.total_ore}h</span>
              </div>
              {hd.entries.map(e => (
                <div key={e.id} className="pj-hist-entry">
                  <span className="pj-hist-entry-main">
                    {e.client_name && <strong>{e.client_name}</strong>}
                    {e.procedura_label && <span className="pj-hist-proc"> — {e.procedura_label}</span>}
                    <span className="pj-hist-activ"> — {e.activitate}</span>
                  </span>
                  <span className="pj-hist-cat">{CATEGORIE_LABEL[e.categorie] || e.categorie}</span>
                  <span className="pj-hist-ore">{e.ore}h</span>
                </div>
              ))}
            </div>
          ))}

          {histHasMore && !filterActive && (
            <div className="pj-hist-more">
              <button className="btn btn-sm btn-secondary" type="button" onClick={loadHistoryMore} disabled={histLoadingMore}>
                {histLoadingMore ? 'Se incarca...' : 'Incarca mai mult'}
              </button>
            </div>
          )}
        </div>
      )}

      </div>

      {toasts.length > 0 && (
        <div className="toast-wrap">
          {toasts.map(t => <div key={t.id} className="toast">{t.msg}</div>)}
        </div>
      )}
    </main>
  );
}
